import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation } from 'react-query'
import { Save, RefreshCw, DollarSign, Percent, Calculator, ShieldCheck, SlidersHorizontal, DatabaseZap } from 'lucide-react'
import {
  VStack, HStack, Text, Heading, Button, Card, CardBody, CardHeader, Flex, Box,
  SimpleGrid, Divider, useToast, FormControl, FormLabel, FormHelperText, Alert, AlertIcon,
  Badge, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper,
  NumberDecrementStepper, Stat, StatLabel, StatNumber, StatHelpText, Switch, Collapse,
  useDisclosure, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react'
import { cmsService } from '@services/api'

const tzs = (n: number) => `TZS ${Math.round(n).toLocaleString('en-US')}`

interface Bounds { chini: number; juu: number }
interface Product {
  product: string
  label: string
  latraCategoryCode: string
  seatRange: { min: number; max: number }
  baseDistanceKm: number
  bounds: { baseFare: Bounds; perKm: Bounds; perMinute: Bounds; levyPct: Bounds }
  values: { baseFare: number; perKm: number; perMinute: number; levyPct: number }
}
interface Config { roundingIncrement: number; products: { daladala_smart: Product } }

const within = (v: number, b: Bounds) => v >= b.chini && v <= b.juu

export default function PricingPage() {
  const toast = useToast()
  const [config, setConfig] = useState<Config | null>(null)
  const [showBands, setShowBands] = useState(false)
  const [preview, setPreview] = useState({ distanceKm: 8.4, durationMin: 22 })

  const { data, isLoading, refetch } = useQuery('latra-tariffs', () => cmsService.getLatraTariffs())

  useEffect(() => {
    const cfg = data?.data ?? data
    if (cfg?.products?.daladala_smart) setConfig(cfg)
  }, [data])

  const p = config?.products?.daladala_smart
  const v = p?.values

  const violations = useMemo(() => {
    if (!p) return [] as string[]
    const out: string[] = []
    const checks: Array<[string, number, Bounds]> = [
      ['Base fare', p.values.baseFare, p.bounds.baseFare],
      ['Per km', p.values.perKm, p.bounds.perKm],
      ['LATRA levy %', p.values.levyPct, p.bounds.levyPct],
    ]
    for (const [label, val, b] of checks) {
      if (!within(val, b)) out.push(`${label} (${val}) is outside the gazetted band [${b.chini}–${b.juu}]`)
    }
    if (p.values.perMinute !== 0 && !within(p.values.perMinute, p.bounds.perMinute)) {
      out.push(`Per minute (${p.values.perMinute}) must be 0 or within [${p.bounds.perMinute.chini}–${p.bounds.perMinute.juu}]`)
    }
    return out
  }, [p])

  const fare = useMemo(() => {
    if (!p || !v) return null
    const extra = Math.max(0, preview.distanceKm - p.baseDistanceKm)
    const metered = extra * v.perKm + preview.durationMin * v.perMinute
    const floorApplied = metered <= v.baseFare
    const tripFare = Math.round(Math.max(v.baseFare, metered))
    const levy = Math.round((tripFare * v.levyPct) / 100)
    return { floorApplied, extra, metered: Math.round(metered), tripFare, levy, net: tripFare }
  }, [p, v, preview])

  const save = useMutation(() => cmsService.updateLatraTariffs(config), {
    onSuccess: () => { toast({ title: 'Pricing updated', status: 'success', duration: 2500 }); refetch() },
    onError: (err: any) => {
      const vlist = err?.response?.data?.violations
      toast({
        title: 'Could not save',
        description: vlist?.length ? vlist.map((x: any) => x.message).join('; ') : (err?.response?.data?.error || 'Update failed'),
        status: 'error', duration: 6000,
      })
    },
  })

  const recalcDialog = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const recalc = useMutation(() => cmsService.recalculateAllFares(), {
    onSuccess: (res: any) => {
      const n = res?.data?.routesUpdated ?? res?.routesUpdated
      toast({
        title: 'Fares recalculated',
        description: n != null ? `${n} route(s) updated to the new prices.` : 'All route fares updated to the new prices.',
        status: 'success',
        duration: 4000,
      })
      recalcDialog.onClose()
    },
    onError: (err: any) => {
      toast({
        title: 'Recalculation failed',
        description: err?.response?.data?.error || err?.message || 'Failed to recalculate fares',
        status: 'error',
        duration: 6000,
      })
      recalcDialog.onClose()
    },
  })

  const setVal = (field: keyof Product['values'], val: number) =>
    setConfig((c) => c && ({ ...c, products: { daladala_smart: { ...c.products.daladala_smart, values: { ...c.products.daladala_smart.values, [field]: val } } } }))
  const setBound = (field: keyof Product['bounds'], edge: keyof Bounds, val: number) =>
    setConfig((c) => c && ({ ...c, products: { daladala_smart: { ...c.products.daladala_smart, bounds: { ...c.products.daladala_smart.bounds, [field]: { ...c.products.daladala_smart.bounds[field], [edge]: val } } } } }))
  const setSeat = (edge: 'min' | 'max', val: number) =>
    setConfig((c) => c && ({ ...c, products: { daladala_smart: { ...c.products.daladala_smart, seatRange: { ...c.products.daladala_smart.seatRange, [edge]: val } } } }))

  if (isLoading || !p || !v) {
    return <Flex justify="center" py={20}><RefreshCw className="spin" /> <Text ml={3}>Loading pricing…</Text></Flex>
  }

  const numField = (label: string, value: number, onChange: (n: number) => void, band: Bounds | null, help: string, step = 100) => {
    const bad = band ? (label.includes('minute') ? value !== 0 && !within(value, band) : !within(value, band)) : false
    return (
      <FormControl isInvalid={bad}>
        <FormLabel fontSize="sm" fontWeight="medium">{label}</FormLabel>
        <NumberInput value={value} step={step} min={0} onChange={(_, n) => onChange(isNaN(n) ? 0 : n)}>
          <NumberInputField />
          <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
        </NumberInput>
        <FormHelperText fontSize="xs" color={bad ? 'red.400' : undefined}>
          {band ? `Gazette band: ${band.chini}–${band.juu}. ` : ''}{help}
        </FormHelperText>
      </FormControl>
    )
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">Pricing control</Heading>
          <Text fontSize="sm" color="gray.500">
            LATRA-aligned fares for {p.label} · seats {p.seatRange.min}–{p.seatRange.max} · applies system-wide to every booking
          </Text>
        </Box>
        <Button leftIcon={<Save size={16} />} colorScheme="blue" isDisabled={violations.length > 0} isLoading={save.isLoading} onClick={() => save.mutate()}>
          Save pricing
        </Button>
      </Flex>

      {violations.length > 0 && (
        <Alert status="error" borderRadius="md"><AlertIcon />
          <Box><Text fontWeight="medium">Fix before saving — outside the gazetted band:</Text>
            {violations.map((x, i) => <Text key={i} fontSize="sm">• {x}</Text>)}
          </Box>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Card>
          <CardHeader pb={2}><HStack><DollarSign size={18} /><Heading size="sm">Base fare & distance</Heading></HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>Base fare is a floor. Fare = greater of base fare or (extra km × per-km + time).</Text>
          </CardHeader>
          <CardBody pt={2}>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              {numField('Base fare (TZS)', v.baseFare, (n) => setVal('baseFare', n), p.bounds.baseFare, 'Charged within the base distance / as the minimum.', 500)}
              {numField('Base distance (km)', p.baseDistanceKm, (n) => setConfig((c) => c && ({ ...c, products: { daladala_smart: { ...c.products.daladala_smart, baseDistanceKm: n } } })), null, 'Distance the base fare covers.', 1)}
              {numField('Fare per km (TZS)', v.perKm, (n) => setVal('perKm', n), p.bounds.perKm, 'Charged for every km beyond the base distance.', 50)}
              {numField('Fare per minute (TZS)', v.perMinute, (n) => setVal('perMinute', n), p.bounds.perMinute, '0 = distance-only charging (blueprint for later).', 10)}
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card>
          <CardHeader pb={2}><HStack><Percent size={18} /><Heading size="sm">Statutory & rounding</Heading></HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>The levy is inclusive in the fare — not added to the rider’s price.</Text>
          </CardHeader>
          <CardBody pt={2}>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              {numField('LATRA levy (%)', v.levyPct, (n) => setVal('levyPct', n), p.bounds.levyPct, 'Inclusive in the fare, remitted to LATRA.', 0.5)}
              {numField('Rounding (TZS)', config!.roundingIncrement, (n) => setConfig((c) => c && ({ ...c, roundingIncrement: Math.max(1, n) })), null, 'Round the trip fare to this increment.', 1)}
            </SimpleGrid>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <HStack><ShieldCheck size={18} /><Heading size="sm">Vehicle category</Heading></HStack>
            <Badge colorScheme="purple">{p.latraCategoryCode}</Badge>
          </Flex>
        </CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
            {numField('Min seats', p.seatRange.min, (n) => setSeat('min', n), null, 'Licensed lower seat limit.', 1)}
            {numField('Max seats', p.seatRange.max, (n) => setSeat('max', n), null, 'Licensed upper seat limit (e.g. 14).', 1)}
            <Box><Text fontSize="sm" fontWeight="medium" mb={1}>Category</Text><Text fontSize="sm" color="gray.500">{p.label}</Text></Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <HStack><SlidersHorizontal size={18} /><Heading size="sm">Regulatory bands (advanced)</Heading></HStack>
            <HStack><Text fontSize="sm" color="gray.500">Edit gazette floor/ceiling</Text><Switch isChecked={showBands} onChange={(e) => setShowBands(e.target.checked)} /></HStack>
          </Flex>
        </CardHeader>
        <Collapse in={showBands}>
          <CardBody pt={0}>
            <Alert status="warning" fontSize="xs" mb={4} borderRadius="md"><AlertIcon />
              These are the LATRA legal limits your prices are validated against. Only change them when the gazette changes.
            </Alert>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={4}>
              {(['baseFare', 'perKm', 'perMinute', 'levyPct'] as const).map((f) => (
                <HStack key={f} justify="space-between">
                  <Text fontSize="sm" minW="120px" textTransform="capitalize">{f.replace(/Pct/, ' %')}</Text>
                  <NumberInput size="sm" value={p.bounds[f].chini} onChange={(_, n) => setBound(f, 'chini', isNaN(n) ? 0 : n)}><NumberInputField placeholder="chini" /></NumberInput>
                  <Text fontSize="sm">–</Text>
                  <NumberInput size="sm" value={p.bounds[f].juu} onChange={(_, n) => setBound(f, 'juu', isNaN(n) ? 0 : n)}><NumberInputField placeholder="juu" /></NumberInput>
                </HStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Collapse>
      </Card>

      <Card>
        <CardHeader pb={2}><HStack><Calculator size={18} /><Heading size="sm">Live fare preview</Heading></HStack></CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Distance (km)</FormLabel>
              <NumberInput value={preview.distanceKm} step={0.5} min={0} onChange={(_, n) => setPreview((s) => ({ ...s, distanceKm: isNaN(n) ? 0 : n }))}><NumberInputField /></NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Duration (min)</FormLabel>
              <NumberInput value={preview.durationMin} step={1} min={0} onChange={(_, n) => setPreview((s) => ({ ...s, durationMin: isNaN(n) ? 0 : n }))}><NumberInputField /></NumberInput>
            </FormControl>
          </SimpleGrid>
          <Divider mb={4} />
          {fare && (
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
              <Stat><StatLabel>Trip fare (rider pays)</StatLabel><StatNumber>{tzs(fare.tripFare)}</StatNumber>
                <StatHelpText>{fare.floorApplied ? 'Base fare (floor)' : `${fare.extra.toFixed(1)} km metered`}</StatHelpText></Stat>
              <Stat><StatLabel>LATRA levy (incl.)</StatLabel><StatNumber>{tzs(fare.levy)}</StatNumber><StatHelpText>{v.levyPct}% · remitted</StatHelpText></Stat>
              <Stat><StatLabel>Driver earnings</StatLabel><StatNumber color="green.500">{tzs(fare.net)}</StatNumber><StatHelpText>full trip fare</StatHelpText></Stat>
            </SimpleGrid>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader pb={2}>
          <HStack><DatabaseZap size={18} /><Heading size="sm">Apply prices to existing routes</Heading></HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            New quotes, trip lists and bookings use these prices immediately. Stored route data
            (route/stop fare labels, seat-selection base fare) refreshes when you run this.
          </Text>
        </CardHeader>
        <CardBody pt={2}>
          <HStack justify="space-between" wrap="wrap" gap={3}>
            <Text fontSize="sm" color="gray.500">
              Recalculates <b>fareFromStart</b> and <b>baseFare</b> for every active route. Safe and reversible.
            </Text>
            <Button
              leftIcon={<RefreshCw size={16} />}
              variant="outline"
              colorScheme="orange"
              isLoading={recalc.isLoading}
              onClick={recalcDialog.onOpen}
            >
              Recalculate all route fares
            </Button>
          </HStack>
        </CardBody>
      </Card>

      <AlertDialog isOpen={recalcDialog.isOpen} leastDestructiveRef={cancelRef} onClose={recalcDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Recalculate all route fares?</AlertDialogHeader>
            <AlertDialogBody>
              This updates the stored <b>fareFromStart</b> and <b>baseFare</b> on every active route using the
              current prices above. It’s reversible — revert the prices and run this again to roll back.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={recalcDialog.onClose}>Cancel</Button>
              <Button colorScheme="orange" ml={3} isLoading={recalc.isLoading} onClick={() => recalc.mutate()}>
                Recalculate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  )
}
